/* ---------------------------------------------------------------------------
   Flood, scour and seismic screening.

   PROVENANCE — read this before quoting a band.

   This is a SCREEN, not an assessment. It ranks the structures in the register
   against each other using the handful of fields the register actually holds,
   so that somebody deciding where to send an engineer has a defensible order to
   work down. It is not a hydraulic study, not a scour computation to any code,
   and not a seismic analysis. Nothing here should ever appear in a document that
   claims otherwise, which is why every band carries the inputs that produced it
   and the sentence that justifies it.

   THREE RULES THIS FILE IS BUILT ON.

   **Missing an input scores `unknown`, never `low`.** A register that answers
   "fine" for a structure it knows nothing about is worse than one that says
   nothing: the first is a wrong answer somebody will act on, the second is a gap
   somebody can go and fill. Every band below has an explicit unknown case, and
   on today's five records it is reached more often than any other — which is the
   honest state of a register transcribed from drawings that do not carry
   foundation type.

   **Nothing is derived across the vertical datum.** The elevation data and the
   register's own levels disagree by between 68 m below and 82 m above at these
   five structures — see the note at the top of src/map/terrain.ts. So the flood
   screen uses `deckRL - hfl` and only that: both numbers come off the same
   drawing, so their DIFFERENCE is sound even though neither level is on a datum
   this application can reconcile. No DEM sample is read here, deliberately.

   **The thresholds are stated, not implied.** Each is a named constant with the
   reason it has that value beside it. One is a code minimum; the other is this
   application's own choice and says so. Anybody who disagrees should change the
   constant and re-read the sentence under it, rather than reverse-engineer a
   number out of the banding.

   WHAT IS NOT SCREENED, and is absent rather than guessed:

     · Design discharge, channel width, bed material, computed scour depth,
       contraction — none are in the register, and all of them are what an actual
       scour assessment turns on.
     · Seismic HAZARD. NBC 105:2020 sets a seismic zoning factor by
       municipality, which this register does not carry. Importing a national
       map into this file would be exactly the invented data the rest of this
       application refuses — and it would not separate these structures from one
       another anyway, because no part of Nepal is a low-hazard zone. What the
       seismic screen ranks is VULNERABILITY: how a structure would behave, not
       how hard it will be shaken. Its reason strings say so.
     · Consequence — traffic, detour length, lifeline status. That is `GR-12`,
       and it is deliberately not folded in here: exposure and consequence are
       two different questions, and one blended number would hide both.
   --------------------------------------------------------------------------- */

import type { Bridge, Inspection } from './types.ts';
import { prettyDate } from './derive.ts';

/**
 * How exposed a structure is on one of the three counts.
 *
 * `unknown` is a real answer and the most common one. It means the register does
 * not hold what this screen needs, which is a finding in its own right — see
 * `screeningGaps`.
 */
export type ScreeningBand = 'unknown' | 'low' | 'moderate' | 'high';

export type ScreeningKey = 'flood' | 'scour' | 'seismic';

/**
 * One input a band was computed from, as it should be shown beside the band.
 *
 * The whole point of carrying these: a screening score nobody can audit is a
 * number somebody will either over-trust or ignore, and both are worse than a
 * short list of the facts it came from. `known: false` marks the fields whose
 * absence is what pushed a facet to `unknown`.
 */
export interface ScreeningInput {
  label: string;
  /** Formatted value, or '—' where the register does not hold it. */
  value: string;
  known: boolean;
}

export interface ScreeningFacet {
  key: ScreeningKey;
  label: string;
  band: ScreeningBand;
  /** One sentence saying why this band and not another. Shown, not logged. */
  reason: string;
  inputs: ScreeningInput[];
}

export interface Screening {
  flood: ScreeningFacet;
  scour: ScreeningFacet;
  seismic: ScreeningFacet;
  /** Every facet, in reading order. */
  facets: ScreeningFacet[];
  /** The one band a pin, a chip or a sort uses. See `overallBand`. */
  overall: ScreeningBand;
}

/* --------------------------- thresholds --------------------------- */

/**
 * Freeboard at or below which the flood screen says `high`, in metres.
 *
 * IRC:SP:13 sets a minimum vertical clearance above the design high flood level
 * that varies with design discharge — from 150 mm on the smallest channels up to
 * 1.5 m above 3000 m³/s. **This register holds no discharge**, so the top of that
 * table is used as a single line: a structure below it is under the code minimum
 * for every discharge band there is, whatever its catchment turns out to be.
 * That makes it a floor, not a compliance check.
 */
export const FREEBOARD_MIN_M = 1.5;

/**
 * Freeboard below which the flood screen says `moderate`, in metres.
 *
 * **Not from any code.** Twice the floor, chosen so that "clear by a comfortable
 * margin" and "clear by inches over the minimum" do not both read as fine. A
 * screen whose only line is the legal minimum says nothing at all until a
 * structure is already non-compliant.
 */
export const FREEBOARD_COMFORT_M = 3.0;

/* --------------------------- vocabulary --------------------------- */

/**
 * What a recorded foundation description amounts to for scour.
 *
 * The register stores these as free text off an inventory sheet, so this reads
 * the words rather than an enumeration. An unrecognised description returns
 * `unknown` — a word this build does not know is not permission to guess, and
 * the alternative (defaulting to one class) would silently mis-band a real
 * structure the first time somebody typed something new.
 */
export type FoundationClass = 'shallow' | 'deep' | 'unknown';

/* Deep is tested FIRST, and the order matters: "open well foundation" is a real
   sheet value and it is a well, not an open footing. */
const DEEP_FOUNDATION = /\b(pile|piled|piles|well|wells|caisson|bored|barrette)\b/i;
const SHALLOW_FOUNDATION = /\b(open|raft|spread|strip|pad|mat|isolated|footing|shallow)\b/i;

export function classifyFoundation(text: string | null | undefined): FoundationClass {
  if (text == null || text.trim() === '') return 'unknown';
  if (DEEP_FOUNDATION.test(text)) return 'deep';
  if (SHALLOW_FOUNDATION.test(text)) return 'shallow';
  return 'unknown';
}

/**
 * Whether the bearings, as recorded, hold the deck against displacement.
 *
 * `sliding` covers the arrangements that let the deck travel relative to the
 * substructure with nothing but friction and seat width resisting it — steel
 * rockers, rollers, sliding plates. That is the classic unseating arrangement
 * and it is what sits under a great many older multi-span decks. `restrained`
 * covers elastomeric, pot and fixed bearings, and monolithic or integral
 * construction.
 *
 * As with foundations, an unrecognised description is `unknown` rather than a
 * default.
 */
export type BearingRestraint = 'sliding' | 'restrained' | 'unknown';

const SLIDING_BEARING = /\b(rocker|rockers|roller|rollers|sliding|slide|ptfe)\b/i;
const RESTRAINED_BEARING =
  /\b(elastomeric|elastomer|neoprene|laminated|rubber|pot|fixed|integral|monolithic)\b/i;

export function classifyBearing(text: string | null | undefined): BearingRestraint {
  if (text == null || text.trim() === '') return 'unknown';
  if (SLIDING_BEARING.test(text)) return 'sliding';
  if (RESTRAINED_BEARING.test(text)) return 'restrained';
  return 'unknown';
}

/* --------------------------- the three screens --------------------------- */

function metres(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(2)} m`;
}

/**
 * Flood: freeboard, and nothing else.
 *
 * The roadmap's own instruction, and it is the right one. `deckRL - hfl` is the
 * one figure here that is internally consistent — both levels come off the same
 * general-arrangement drawing, on whatever benchmark that drawing used — so the
 * difference means something even though neither level can be placed on a
 * national datum. Bringing the DEM in would produce a number derived from two
 * vertical references that disagree by up to 82 m at these very structures.
 *
 * The one caveat that cannot be computed away is carried on the input instead:
 * `deckRL` is DECK level, and codes measure clearance to the SOFFIT. The real
 * clearance is therefore smaller than this figure by the depth of the
 * superstructure, which the register does not record. Every band below is
 * consequently optimistic, and by more on a deep box girder than on a slab.
 */
function floodFacet(b: Bridge): ScreeningFacet {
  const freeboard = b.deckRL != null && b.hfl != null ? Number((b.deckRL - b.hfl).toFixed(2)) : null;

  const inputs: ScreeningInput[] = [
    { label: 'Deck level', value: metres(b.deckRL), known: b.deckRL != null },
    { label: 'Highest flood level', value: metres(b.hfl), known: b.hfl != null },
    {
      label: 'Freeboard',
      value: metres(freeboard),
      known: freeboard != null,
    },
  ];

  if (freeboard == null) {
    return {
      key: 'flood',
      label: 'Flood',
      band: 'unknown',
      reason:
        b.deckRL == null && b.hfl == null
          ? 'Deck level and HFL not recorded.'
          : b.deckRL == null
            ? 'Deck level not recorded.'
            : 'HFL not recorded.',
      inputs,
    };
  }

  if (freeboard <= FREEBOARD_MIN_M) {
    return {
      key: 'flood',
      label: 'Flood',
      band: 'high',
      reason: `${freeboard.toFixed(2)} m freeboard, at or below the ${FREEBOARD_MIN_M.toFixed(1)} m minimum.`,
      inputs,
    };
  }

  if (freeboard < FREEBOARD_COMFORT_M) {
    return {
      key: 'flood',
      label: 'Flood',
      band: 'moderate',
      reason: `${freeboard.toFixed(2)} m freeboard, under ${FREEBOARD_COMFORT_M.toFixed(1)} m.`,
      inputs,
    };
  }

  return {
    key: 'flood',
    label: 'Flood',
    band: 'low',
    reason: `${freeboard.toFixed(2)} m freeboard.`,
    inputs,
  };
}

/** The latest underwater / NSTM inspection on file, or null. */
function lastUnderwater(b: Bridge): Inspection | null {
  return b.inspections.reduce<Inspection | null>(
    (best, i) => (i.type === 'underwater' && (best === null || i.date > best.date) ? i : best),
    null,
  );
}

/**
 * Scour: what stands in the water, what it stands on, and whether anyone has
 * ever looked.
 *
 * Three facts, in that order of weight.
 *
 * **A structure with no intermediate support has nothing in the channel**, which
 * is the strongest single protective fact about a Nepali crossing and one the
 * register knows for every record it holds. It is not immunity — abutments and
 * their protection works scour too — and the reason says so.
 *
 * **A shallow foundation in an alluvial bed is the failure**, not a contributor
 * to it. No combination of other recorded fields should pull that back below
 * `high`.
 *
 * **A deep foundation nobody has inspected below water is an assumption, not a
 * finding.** Scour at a well or a pile cap is invisible from the deck: the
 * structure looks exactly the same the day before it goes. So a well-founded
 * pier with an underwater inspection on file screens `low`, and the same pier
 * with none screens `moderate`. That difference is the whole argument for
 * carrying `underwater` as an inspection tier of its own.
 */
function scourFacet(b: Bridge): ScreeningFacet {
  const spans = Number.isFinite(b.spans) ? b.spans : null;
  const piers = spans != null && spans >= 1 ? spans - 1 : null;
  const foundation = classifyFoundation(b.pierFoundation);
  const dive = lastUnderwater(b);

  const inputs: ScreeningInput[] = [
    {
      label: 'Supports in the channel',
      value: piers == null ? '—' : String(piers),
      known: piers != null,
    },
    {
      label: 'Pier foundation',
      value: b.pierFoundation ?? '—',
      known: foundation !== 'unknown',
    },
    {
      label: 'Abutment foundation',
      value: b.abutmentFoundation ?? '—',
      known: b.abutmentFoundation != null,
    },
    {
      label: 'River training',
      value: b.riverTraining ?? '—',
      known: b.riverTraining != null,
    },
    {
      label: 'Last underwater inspection',
      value: dive ? prettyDate(dive.date) : 'none on file',
      known: dive != null,
    },
  ];

  if (piers == null) {
    return {
      key: 'scour',
      label: 'Scour',
      band: 'unknown',
      reason: 'Number of spans not recorded.',
      inputs,
    };
  }

  const supports = `${piers} support${piers === 1 ? '' : 's'} in the channel`;

  if (piers === 0) {
    return {
      key: 'scour',
      label: 'Scour',
      band: 'low',
      reason: 'Single span, nothing in the channel.',
      inputs,
    };
  }

  if (foundation === 'unknown') {
    return {
      key: 'scour',
      label: 'Scour',
      band: 'unknown',
      reason: `${supports}, pier foundation type not known.`,
      inputs,
    };
  }

  if (foundation === 'shallow') {
    return {
      key: 'scour',
      label: 'Scour',
      band: 'high',
      reason: `${supports} on a shallow foundation.`,
      inputs,
    };
  }

  if (dive == null) {
    return {
      key: 'scour',
      label: 'Scour',
      band: 'moderate',
      reason: `${supports} on a deep foundation, no underwater inspection on file.`,
      inputs,
    };
  }

  return {
    key: 'scour',
    label: 'Scour',
    band: 'low',
    reason: `${supports} on a deep foundation, inspected underwater ${prettyDate(dive.date)}.`,
    inputs,
  };
}

/**
 * Seismic: how the structure would behave, not how hard it will be shaken.
 *
 * **Hazard is not screened here, and the reason strings say so** — see the
 * header. What is left is vulnerability, and the mechanism worth ranking is
 * unseating: a span dropping off its support. That is what took bridges down in
 * 2015 and it is the one thing the register's own fields speak to.
 *
 * **A multi-span structure cannot score better than `moderate` here**, however
 * good its bearings, because the register does not record whether the deck is
 * continuous over its piers. Continuity is most of the answer and it is not on
 * file. Inferring it from a superstructure description ("cast in situ", "box
 * girder") would be a guess dressed up as a derivation.
 */
function seismicFacet(b: Bridge): ScreeningFacet {
  const spans = Number.isFinite(b.spans) ? b.spans : null;
  const restraint = classifyBearing(b.bearings);

  const inputs: ScreeningInput[] = [
    {
      label: 'Spans',
      value: spans == null ? '—' : String(spans),
      known: spans != null,
    },
    {
      label: 'Bearings',
      value: b.bearings ?? '—',
      known: restraint !== 'unknown',
    },
    {
      label: 'Movement provision',
      value: b.movement ?? '—',
      known: b.movement != null,
    },
  ];

  if (spans == null || spans < 1) {
    return {
      key: 'seismic',
      label: 'Seismic',
      band: 'unknown',
      reason: 'Number of spans not recorded.',
      inputs,
    };
  }

  if (spans === 1) {
    return {
      key: 'seismic',
      label: 'Seismic',
      band: 'low',
      reason: 'Single span, no intermediate support to unseat.',
      inputs,
    };
  }

  if (restraint === 'unknown') {
    return {
      key: 'seismic',
      label: 'Seismic',
      band: 'unknown',
      reason: `${spans} spans, bearing type not known.`,
      inputs,
    };
  }

  if (restraint === 'sliding') {
    return {
      key: 'seismic',
      label: 'Seismic',
      band: 'high',
      reason: `${spans} spans on sliding bearings — unseating risk.`,
      inputs,
    };
  }

  return {
    key: 'seismic',
    label: 'Seismic',
    band: 'moderate',
    reason: `${spans} spans on restrained bearings. Deck continuity is not recorded.`,
    inputs,
  };
}

/* --------------------------- the whole screen --------------------------- */

const KNOWN_ORDER: Record<Exclude<ScreeningBand, 'unknown'>, number> = {
  low: 0,
  moderate: 1,
  high: 2,
};

/**
 * The one band that stands for all three.
 *
 * Two rules, and both matter more than they look:
 *
 * **`high` outranks `unknown`.** Knowing enough to be worried is a stronger
 * statement than not knowing, and a structure with a shallow-founded pier in the
 * river must not be greyed out on a map because nobody recorded its bearings.
 *
 * **`unknown` outranks `moderate` and `low`.** A structure with one facet
 * unscreenable is not a moderate structure; it is a structure that has not been
 * screened. Reporting the worst KNOWN band as though it were the answer is
 * exactly the "a missing input reads as fine" failure this whole file exists to
 * avoid, one level up.
 */
export function overallBand(bands: readonly ScreeningBand[]): ScreeningBand {
  if (bands.length === 0) return 'unknown';
  if (bands.includes('high')) return 'high';
  if (bands.includes('unknown')) return 'unknown';
  return bands.reduce((worst, b) =>
    KNOWN_ORDER[b as Exclude<ScreeningBand, 'unknown'>] >
    KNOWN_ORDER[worst as Exclude<ScreeningBand, 'unknown'>]
      ? b
      : worst,
  );
}

export function screenBridge(b: Bridge): Screening {
  const flood = floodFacet(b);
  const scour = scourFacet(b);
  const seismic = seismicFacet(b);
  const facets = [flood, scour, seismic];
  return { flood, scour, seismic, facets, overall: overallBand(facets.map((f) => f.band)) };
}

/* --------------------------- presentation --------------------------- */

/** The bands in the order a legend or a rollup should read them, worst first. */
export const BANDS: readonly ScreeningBand[] = ['high', 'moderate', 'low', 'unknown'];

const BAND_LABELS: Record<ScreeningBand, string> = {
  unknown: 'Not screened',
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
};

export function bandLabel(band: ScreeningBand): string {
  return BAND_LABELS[band];
}

/**
 * Where a band sits on the chip ramp.
 *
 * `unknown` is deliberately NOT on it. The ramp is a rank — darker is worse —
 * and "we do not know" is not a position on that scale. Putting it at step 2
 * would make an unscreened structure read as mildly concerning, which is both
 * wrong and reassuring. `'none'` is the chip outside the ramp; see `tag` in
 * src/components/widgets.ts.
 */
export function bandRank(band: ScreeningBand): 1 | 2 | 3 | 4 | 'none' {
  switch (band) {
    case 'low':
      return 1;
    case 'moderate':
      return 3;
    case 'high':
      return 4;
    case 'unknown':
      return 'none';
  }
}

/** How many structures fall in each band, for a legend or a rollup. */
export function bandCounts(list: readonly Bridge[]): Record<ScreeningBand, number> {
  const out: Record<ScreeningBand, number> = { unknown: 0, low: 0, moderate: 0, high: 0 };
  for (const b of list) out[screenBridge(b).overall]++;
  return out;
}

/**
 * Which fields, across the register, are stopping structures being screened.
 *
 * The most useful thing this module produces on today's data, and the reason
 * `unknown` is a first-class band rather than a fallback: it turns "these cannot
 * be screened" into a list of columns somebody can go and fill in. Ordered by
 * how many structures each would unblock.
 */
export function screeningGaps(list: readonly Bridge[]): { field: string; count: number }[] {
  const counts = new Map<string, number>();
  const bump = (field: string): void => void counts.set(field, (counts.get(field) ?? 0) + 1);

  for (const b of list) {
    const s = screenBridge(b);
    if (s.flood.band === 'unknown') {
      if (b.deckRL == null) bump('Deck level');
      if (b.hfl == null) bump('HFL');
    }
    const multiSpan = b.spans != null && Number.isFinite(b.spans) && b.spans > 1;
    if (s.scour.band === 'unknown' && multiSpan) bump('Pier foundation');
    if (s.seismic.band === 'unknown' && multiSpan) bump('Bearings');
  }

  return Array.from(counts, ([field, count]) => ({ field, count })).sort(
    (a, b) => b.count - a.count || a.field.localeCompare(b.field),
  );
}
